/** Municipality-first landing page. */

import { useMemo, useState } from "react";

import { useGemeinden } from "../hooks/useGemeinden";
import { useLanguageContext } from "../hooks/useLanguage";
import type { RegionConfig } from "../types";
import { AboutPanel } from "./AboutPanel";
import { MeasureCatalogueModal } from "./MeasureCatalogueModal";
import { VersionBadge } from "./VersionBadge";

interface MunicipalitySelectProps {
  config: RegionConfig;
  /** Receives the selected municipality's stable UID (not its name). */
  onSelect: (uid: string) => void;
  onTourStart: () => void;
}

export function MunicipalitySelect({ config, onSelect, onTourStart }: MunicipalitySelectProps) {
  const { municipalities: unsortedMunicipalities, loading, error } = useGemeinden();
  const { t, lang, toggleLang } = useLanguageContext();
  const [search, setSearch] = useState("");

  const municipalities = useMemo(() => {
    return [...unsortedMunicipalities].sort((first, second) =>
      first.name.localeCompare(second.name, lang),
    );
  }, [lang, unsortedMunicipalities]);

  // Names that occur more than once — only these need an admin subtitle to be
  // told apart. In a single-region deployment this set is empty (clean list).
  const duplicateNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const option of municipalities) {
      counts.set(option.name, (counts.get(option.name) ?? 0) + 1);
    }
    return new Set([...counts].filter(([, n]) => n > 1).map(([name]) => name));
  }, [municipalities]);

  const filteredMunicipalities = useMemo(() => {
    const query = search.trim().toLocaleLowerCase(lang);
    if (!query) return [];
    return municipalities.filter((option) =>
      option.name.toLocaleLowerCase(lang).includes(query),
    );
  }, [lang, municipalities, search]);

  const showResults = search.trim().length > 0 && filteredMunicipalities.length > 0;
  const showEmpty =
    search.trim().length > 0 && filteredMunicipalities.length === 0 && !loading;

  return (
    <div className="flima-min-screen relative flex flex-col overflow-x-hidden bg-flima-mist">
      <a
        href="#main-content"
        className="sr-only z-50 rounded-md bg-white px-4 py-2 text-flima-spruce focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus-visible:ring-2 focus-visible:ring-flima-river"
      >
        {t("skip_to_content")}
      </a>

      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[46vh] bg-gradient-to-br from-flima-spruce via-flima-spruce to-flima-river"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute right-[-8rem] top-[-10rem] hidden h-96 w-96 rounded-full border-[3rem] border-white/5 sm:block"
        aria-hidden="true"
      />

      {/* z-40 (not z-10): the header owns the About/Catalogue dropdowns, whose
          own z-index is capped by this stacking context. At z-10 it tied with
          <main> (also z-10) and lost on DOM order, rendering the dropdown behind
          the page content so clicks fell through and closed it. */}
      <header className="relative z-40 border-b border-white/15 text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-3 py-4 sm:gap-4 sm:px-8 sm:py-5">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="h-10 w-1 shrink-0 rounded-full bg-gradient-to-b from-white via-flima-river to-flima-lichen"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p className="truncate font-display text-lg font-semibold tracking-tight sm:text-xl">
                {config.branding.title}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={toggleLang}
              className="min-h-11 rounded-md border border-white/30 bg-white/10 px-3 font-utility text-xs font-medium text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-flima-spruce motion-reduce:transition-none"
              aria-label={t("language_toggle")}
            >
              {lang === "de" ? "EN" : "DE"}
            </button>
            <MeasureCatalogueModal config={config} />
            <AboutPanel config={config} />
          </div>
        </div>
      </header>

      <main
        id="main-content"
        className="relative z-20 flex flex-1 flex-col items-center justify-center px-4 py-8 sm:px-8 sm:py-16"
      >
        <p className="mb-5 max-w-4xl text-center text-pretty font-display text-xl font-semibold leading-tight text-white min-[360px]:text-2xl sm:mb-6 sm:text-3xl">
          {t("tagline")}
        </p>

        <section
          data-tour-id="landing-tour-card"
          className="w-full max-w-xl rounded-2xl border border-white/70 bg-white/95 p-4 shadow-panel backdrop-blur min-[360px]:p-6 sm:p-9"
        >
          <div className="mb-6 flex items-start gap-3 sm:mb-7 sm:gap-4">
            <span
              className="mt-1 h-14 w-1 shrink-0 rounded-full bg-gradient-to-b from-flima-river to-flima-lichen"
              aria-hidden="true"
            />
            <div>
              <h1 className="text-pretty font-display text-2xl font-semibold leading-tight text-flima-spruce sm:text-3xl">
                {t("municipality_prompt")}
              </h1>
            </div>
          </div>

          <label
            htmlFor="municipality-search"
            className="mb-2 block font-display text-sm font-semibold text-flima-slate"
          >
            {t("search_label")}
          </label>
          <div className="relative" data-tour-id="municipality-search">
            <svg
              viewBox="0 0 24 24"
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-flima-river"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-4-4" />
            </svg>
            <input
              id="municipality-search"
              name="municipality"
              type="search"
              autoComplete="off"
              placeholder={t("search_placeholder")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-controls="municipality-results"
              aria-expanded={showResults}
              className="min-h-14 w-full rounded-lg border border-flima-spruce/20 bg-white py-3 pl-12 pr-4 text-base text-flima-slate shadow-sm placeholder:text-flima-slate/45 hover:border-flima-river focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2"
            />

            {showResults ? (
              <ul
                id="municipality-results"
                className="absolute z-50 mt-2 max-h-72 w-full overflow-y-auto overscroll-contain rounded-lg border border-flima-spruce/10 bg-white p-1.5 shadow-float [content-visibility:auto] [contain-intrinsic-size:0_18rem]"
              >
                {filteredMunicipalities.map((option) => (
                  <li key={option.uid}>
                    <button
                      type="button"
                      onClick={() => onSelect(option.uid)}
                      className="min-h-11 w-full rounded-md px-3 py-2 text-left text-sm font-semibold text-flima-slate transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-flima-river motion-reduce:transition-none"
                    >
                      {option.name}
                      {duplicateNames.has(option.name) && option.region ? (
                        <span className="mt-0.5 block text-xs font-normal text-flima-slate/60">
                          {option.region}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="min-h-8" aria-live="polite">
            {showEmpty ? (
              <p className="mt-3 text-sm text-flima-slate/70">{t("no_results")}</p>
            ) : null}
            {loading ? (
              <p className="mt-3 text-sm text-flima-slate/70" role="status">
                {t("loading")}
              </p>
            ) : null}
            {error ? (
              <p className="mt-3 break-words text-sm font-semibold text-red-700" role="alert">
                {t("error")}: {error}
              </p>
            ) : null}
          </div>

          <p className="mt-6 border-t border-flima-spruce/10 pt-4 text-xs text-flima-slate/55">
            {t("under_construction")}
          </p>
          <button
            type="button"
            onClick={onTourStart}
            className="mt-4 flex min-h-11 w-full items-center justify-center rounded-lg bg-flima-spruce px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-flima-river focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            {t("tour_start")}
          </button>
        </section>
      </main>

      {/* Logos — top row: Gobio, BW, Inula in a single flex line (BW centered).
          Bottom: Powered by OekoFor, shifted left of center. */}
      <footer className="relative z-10 flex shrink-0 flex-col items-center gap-3 px-4 pb-5 pt-3 sm:gap-4 sm:px-5 sm:pb-6 sm:pt-4">
        <div className="flex -translate-x-[75px] -translate-y-6 items-center justify-center gap-6 sm:-translate-x-[75px] sm:-translate-y-16 sm:gap-12">
          <a
            href="https://www.gobio-online.de/"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0"
          >
            <img
              src="/assets/logos/Gobio_logo_transparent.png"
              alt="Gobio"
              className="h-24 w-auto max-w-none transition-opacity hover:opacity-80 min-[360px]:h-28 sm:h-40"
            />
          </a>
          <a
            href="https://rp.baden-wuerttemberg.de/rpf/"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0"
          >
            <img
              src="/assets/logos/Logo_RPFR_transparent.png"
              alt="Regierungspräsidium Freiburg"
              className="h-32 w-auto max-w-none rounded-lg transition-opacity hover:opacity-80 min-[360px]:h-36 sm:h-52"
            />
          </a>
          <a
            href="https://inula.de/"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0"
          >
            <img
              src="/assets/logos/LOGO_INULA_2019.png"
              alt="Inula"
              className="h-32 w-auto max-w-none translate-x-1 transition-opacity hover:opacity-80 min-[360px]:h-36 sm:h-52 sm:translate-x-2.5"
            />
          </a>
        </div>
        {/* Powered by OekoFor — below the BW logo, shifted left of center */}
        <div className="flex -translate-x-[25px] -translate-y-6 items-center gap-2 sm:-translate-y-10 sm:gap-3">
          <span className="whitespace-nowrap text-sm font-semibold text-flima-slate/70">{t("powered_by")}</span>
          <a
            href="https://www.oekofor.de/de/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/assets/logos/oekofor_logo.png"
              alt="OekoFor"
              className="h-16 w-auto shrink-0 object-contain transition-opacity hover:opacity-80 sm:h-20"
            />
          </a>
        </div>
        <VersionBadge className="absolute bottom-2 right-3 sm:bottom-3 sm:right-4" />
      </footer>
    </div>
  );
}
